import { Dialog } from '@base-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { Button, ButtonVariants } from '@components/button/Button';
import { ControlledFormInput } from '@components/formInputs/ControlledFormInput';
import { ControlledFormPhoneInput } from '@components/formInputs/ControlledFormPhoneInput';
import { LayoutVariant } from '@components/formInputs/sharedTypes';
import { GuestCountPicker } from '@components/guestCountPicker/GuestCountPicker';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { reportAppError } from '@shared/reportAppError';
import { DESKTOP1280 } from '@shared/styles/breakpoints';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { RESERVATIONS_WAITLIST_PAGE_PATH } from 'restaurantAdmin/paths';
import styles from './AddToWaitlistDrawer.scss';
import { createWaitListEntry } from './apiHelpers';
import { useWaitListContext } from './state/WaitListContext';

export interface WaitListFormData {
  estimatedWait: string;
  firstName: string;
  guestCount: number;
  guestPhone: string;
  lastName: string;
}

const DEFAULT_GUEST_COUNT = 2;

export const AddToWaitlistDrawer = () => {
  const navigate = useNavigate();

  const { control, handleSubmit, reset } = useForm<WaitListFormData>({
    defaultValues: {
      guestCount: DEFAULT_GUEST_COUNT,
    },
  });

  const { fetchWaitListEntries } = useWaitListContext();
  const { id: restaurantId, maxReservationGuests } = useRestaurant();

  const onSubmit = async (data: WaitListFormData) => {
    try {
      await createWaitListEntry({
        estimatedWait: parseInt(data.estimatedWait, 10),
        firstName: data.firstName || null,
        guestCount: data.guestCount,
        lastName: data.lastName || null,
        phone: data.guestPhone,
        restaurantId,
      });
    } catch (e) {
      if (e instanceof ApiError) {
        errorToast({ message: e.message });
      }
      reportAppError(e);
      return;
    }
    successToast({ message: 'Successfully added guest to wait list' });
    fetchWaitListEntries();
    reset();
    navigate(RESERVATIONS_WAITLIST_PAGE_PATH);
  };

  const layoutVariant = useMediaQuery({ minWidth: DESKTOP1280 })
    ? LayoutVariant.Horizontal
    : LayoutVariant.Vertical;

  return (
    <form
      className={styles.container}
      onSubmit={handleSubmit(onSubmit)}
      aria-label="Wait List Form"
    >
      <div className={styles.headerBar}>
        <h2>Add to Waitlist</h2>

        <div className={styles.headerButtons}>
          <Dialog.Close
            render={({ onClick }) => (
              <Button
                label="Cancel"
                onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
                variant={ButtonVariants.Tertiary}
              />
            )}
          />

          <Button
            type="submit"
            label="Add to Waitlist"
            variant={ButtonVariants.Primary}
          />
        </div>
      </div>

      <div className={styles.inputContainer}>
        <ControlledFormPhoneInput
          className={styles.waitListInput}
          control={control}
          label="Guest Phone"
          name="guestPhone"
          rules={{ required: true }}
          variant={layoutVariant}
        />
        <ControlledFormInput
          className={styles.waitListInput}
          control={control}
          disabled={false}
          label="First Name (optional)"
          name="firstName"
          type="text"
          variant={layoutVariant}
        />
        <ControlledFormInput
          className={styles.waitListInput}
          control={control}
          disabled={false}
          label="Last Name (optional)"
          name="lastName"
          type="text"
          variant={layoutVariant}
        />
        <ControlledFormInput
          className={styles.waitListInput}
          control={control}
          disabled={false}
          label="Wait (in minutes)"
          name="estimatedWait"
          rules={{
            required: true,
            min: {
              value: 1,
              message: 'Wait time must be greater than 0',
            },
            max: {
              value: 599,
              message: 'Wait time must be less than 600',
            },
          }}
          type="number"
          variant={layoutVariant}
        />

        <Controller
          control={control}
          name="guestCount"
          render={({ field: { onChange, value } }) => (
            <GuestCountPicker
              guestCount={value}
              max={maxReservationGuests}
              onChange={onChange}
            />
          )}
        />
      </div>
    </form>
  );
};
